import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@mui/material';
import { FaCheckCircle } from 'react-icons/fa';
import { HiOutlineClipboardDocumentCheck, HiOutlineUserGroup, HiOutlineCurrencyDollar } from 'react-icons/hi2';
import { GiCheckMark } from 'react-icons/gi';
import QuoteForm from './QuoteForm';
import nurse1 from '../../assets/img/home/nurse1.png';
import './QuoteLanding.css';

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '¿Cuánto cuesta cotizar con varias enfermeras?',
      acceptedAnswer: { '@type': 'Answer', text: 'Cotizar es completamente gratis y sin compromiso. Solo pagas si decides agendar con alguna de las enfermeras que te respondan.' }
    },
    {
      '@type': 'Question',
      name: '¿Cuánto demoran en llegar las cotizaciones?',
      acceptedAnswer: { '@type': 'Answer', text: 'En un máximo de 45 minutos hábiles empiezas a recibir cotizaciones de las enfermeras disponibles en tu comuna, cada una con su precio y qué incluye su atención.' }
    },
    {
      '@type': 'Question',
      name: '¿Qué pasa si ninguna cotización me convence?',
      acceptedAnswer: { '@type': 'Answer', text: 'No hay ningún compromiso. Puedes revisar las cotizaciones que lleguen y elegir la que más te acomode, o simplemente no continuar si ninguna se ajusta a lo que buscas.' }
    },
    {
      '@type': 'Question',
      name: '¿Las enfermeras que cotizan están certificadas?',
      acceptedAnswer: { '@type': 'Answer', text: 'Sí. Todas las enfermeras registradas en especialistaenheridas.cl pasan por un proceso de verificación de credenciales y experiencia antes de poder atender solicitudes.' }
    },
  ],
};

export default function QuoteLanding() {
  const [openForm, setOpenForm] = useState(false);

  return (
    <>
      <Helmet>
        <title>Cotiza tu Enfermera a Domicilio — Compara Precios | Especialista en Heridas</title>
        <meta name="description" content="Cuéntanos qué procedimiento de enfermería necesitas y recibe cotizaciones de varias enfermeras certificadas en tu comuna. Compara precio y disponibilidad, sin compromiso." />
        <link rel="canonical" href="https://especialistaenheridas.cl/cotiza-tu-enfermera" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="quote_landing">

        <section className="ql_hero">
          <div className="ql_hero_text">
            <span className="ql_eyebrow">Cotización gratuita y sin compromiso</span>
            <h1>Cotiza tu enfermera a domicilio y compara antes de decidir</h1>
            <p className="ql_subtitle">
              Cuéntanos qué necesitas una sola vez. Varias enfermeras certificadas de tu comuna te envían su
              propia cotización — tú eliges con cuál agendar según precio, disponibilidad y experiencia.
            </p>
            <Button
              variant="contained"
              size="large"
              className="ql_cta"
              onClick={() => setOpenForm(true)}
            >
              Completa tu formulario aquí
            </Button>
          </div>
          <img src={nurse1} alt="Enfermera especialista en curación de heridas a domicilio" className="ql_hero_img" />
        </section>

        <section className="ql_section">
          <h2 className="section_title">¿Por qué cotizar antes de agendar?</h2>
          <p className="ql_section_sub">Un solo formulario, varias respuestas — tú decides con más información.</p>

          <div className="items_container">
            <div className="item_box">
              <div className="item_box_header">
                <div className="item_box_icon"><HiOutlineCurrencyDollar size={24} /></div>
                <div>
                  <h2>Compara precios reales</h2>
                  <span>No un valor estimado</span>
                </div>
              </div>
              <p>Cada enfermera que te cotiza indica su precio real para tu procedimiento, no un rango genérico. Puedes comparar antes de comprometerte.</p>
            </div>

            <div className="item_box">
              <div className="item_box_header">
                <div className="item_box_icon"><HiOutlineUserGroup size={24} /></div>
                <div>
                  <h2>Varias opciones, una sola vez</h2>
                  <span>Sin repetir el formulario</span>
                </div>
              </div>
              <p>No necesitas contactar profesional por profesional. Llenas un formulario y las enfermeras disponibles en tu comuna te responden directamente.</p>
            </div>

            <div className="item_box">
              <div className="item_box_header">
                <div className="item_box_icon"><HiOutlineClipboardDocumentCheck size={24} /></div>
                <div>
                  <h2>Sin compromiso</h2>
                  <span>Tú tienes la última palabra</span>
                </div>
              </div>
              <p>Cotizar no te obliga a agendar. Revisas las propuestas con calma y decides si alguna se ajusta a lo que necesitas.</p>
            </div>
          </div>
        </section>

        <section className="ql_section ql_steps">
          <h2 className="section_title">¿Cómo funciona?</h2>
          <div className="ql_steps_grid">
            <div className="ql_step">
              <div className="ql_step_number">1</div>
              <h3>Cuéntanos qué necesitas</h3>
              <p>Procedimiento, comuna, fecha y horario en que te acomoda recibir la atención.</p>
            </div>
            <div className="ql_step">
              <div className="ql_step_number">2</div>
              <h3>Recibe cotizaciones</h3>
              <p>En un máximo de 45 minutos hábiles, enfermeras disponibles en tu comuna te envían su precio y qué incluye su atención.</p>
            </div>
            <div className="ql_step">
              <div className="ql_step_number">3</div>
              <h3>Elige y agenda</h3>
              <p>Compara las propuestas y coordina directamente con la enfermera que más te acomode.</p>
            </div>
          </div>
        </section>

        <section className="ql_section">
          <h2 className="section_title">Preguntas frecuentes</h2>
          <div className="ql_faq">
            {faqJsonLd.mainEntity.map((item, i) => (
              <div className="ql_faq_item" key={i}>
                <h3><GiCheckMark size={16} color="#135095" /> {item.name}</h3>
                <p>{item.acceptedAnswer.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="ql_final_cta">
          <FaCheckCircle size={30} color="#fff" />
          <h2>Cuéntanos qué necesitas y empieza a recibir cotizaciones</h2>
          <Button
            variant="contained"
            size="large"
            className="ql_cta ql_cta_light"
            onClick={() => setOpenForm(true)}
          >
            Completa tu formulario aquí
          </Button>
        </section>

      </div>

      <QuoteForm open={openForm} onClose={() => setOpenForm(false)} />
    </>
  );
}
